import { Modal } from "../../../atom/Modal";
import { EditInformationModalContent } from "./EditInformationModalContent";
import { successToast } from "../../../../utils/toast";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePutResource } from "../../../../services";
import { useUserContext } from "../../../../utils/contexts/UserProvider";

interface EditInformationModalProps {
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleConfirmData: () => Promise<void>;
}

export interface EditInformationModalState {
  first_name: string;
  last_name: string;
  phone_number: string;
  street: string;
  blockNumber: string | number;
  apartmentNumber: string | number;
  postalCode: string | number;
  city: string;
}

export const EditAddressModal: React.FC<EditInformationModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  handleConfirmData,
}) => {
  const { t } = useTranslation();
  const { user, setUser } = useUserContext();
  const { updateResource } = usePutResource("/common/user");
  const { updateResource: updateParticipantAgentResource } = usePutResource(
    "/common/agent-participant",
  );

  const [
    { first_name, last_name, phone_number, city, postalCode, street, blockNumber, apartmentNumber },
    setInformation,
  ] = useState<EditInformationModalState>({
    first_name: user?.first_name ?? "",
    last_name: user?.last_name ?? "",
    phone_number: user?.phone_number ?? "",
    street: user?.street ?? "",
    blockNumber: user?.block_number ?? "",
    apartmentNumber: user?.apartment_number ?? "",
    postalCode: user?.postal_code ?? "",
    city: user?.city ?? "",
  });

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={() => setIsModalOpen(false)}
      title={t("Edit Information")}
      subtitle=""
      content={
        <EditInformationModalContent
          first_name={first_name}
          last_name={last_name}
          phone_number={phone_number}
          setInformation={setInformation}
          street={street}
          blockNumber={blockNumber}
          apartmentNumber={apartmentNumber}
          postalCode={postalCode}
          city={city}
        />
      }
      firstButtonLabel={t("Cancel")}
      firstButtonType="tertiary"
      firstButtonClick={() => setIsModalOpen(false)}
      secondButtonClick={async () => {
        const updateUserRequest = updateResource({
          resourceId: user?.id,
          first_name,
          last_name,
          phone_number,
          street,
          block_number: blockNumber,
          apartment_number: apartmentNumber,
          postal_code: postalCode,
          is_agreement_accepted: false,
          city,
        });

        const requests = user?.participant_agents?.map((participantAgent) =>
          updateParticipantAgentResource({
            resourceId: participantAgent.id,
            latest_accepted_agreement: null,
          }),
        );

        await Promise.all([updateUserRequest, ...(requests ?? [])]);

        // SWR doesn't revalidate/refetch so we optimistically update
        setUser((prev) => {
          if (!prev) {
            return null;
          }

          return {
            ...prev,
            first_name,
            last_name,
            phone_number,
            street,
            block_number: blockNumber as number,
            apartment_number: apartmentNumber as number,
            postal_code: postalCode as number,
            is_agreement_accepted: false,
            participant_agents: prev?.participant_agents?.map((agent) => ({
              ...agent,
              latest_accepted_agreement: null,
            })),
            city,
          };
        });

        await handleConfirmData();

        setIsModalOpen(false);
        successToast(t("Changes saved"));
      }}
      secondButtonLabel={t("Save Changes")}
    />
  );
};
